// src/pages/Programs.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import AppNav from '../components/AppNav';
import { fetchPrograms, fetchSiteContent, fetchPods, getAvailablePods, getPodSaleStatus, generateSlug } from '../services/firebase';
import MOCK_PROGRAMS from '../data/mockPrograms';
import FormattedText from '../components/FormattedText';

function Programs({ demoMode }) {
    const navigate = useNavigate();
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [navLabel, setNavLabel] = useState('');
    const [podsByProgram, setPodsByProgram] = useState({});

    const clerkData = useUser();
    let purchasedIds = [];
    let isAdmin = false;
    let isFriend = false;
    let userId = '';
    if (!demoMode) {
        const user = clerkData?.user;
        purchasedIds = user?.publicMetadata?.purchasedPrograms || [];
        isAdmin = user?.publicMetadata?.role === 'admin';
        isFriend = user?.publicMetadata?.role === 'friend';
        userId = user?.id || '';
    } else {
        purchasedIds = ['mock-1'];
        isAdmin = true;
        userId = 'demo';
    }

    useEffect(() => {
        fetchPrograms().then(async (p) => {
            const progs = (p || MOCK_PROGRAMS)
                .sort((a, b) => (a.order || 999) - (b.order || 999));
            setPrograms(progs);
            const podMap = {};
            for (const prog of progs.filter((pr) => pr.programType === 'cohort')) {
                const pods = await fetchPods(prog.id);
                podMap[prog.id] = pods;
            }
            setPodsByProgram(podMap);
            setLoading(false);
        });
        fetchSiteContent().then((c) => {
            if (c && c.dashboardNavLabel) setNavLabel(c.dashboardNavLabel);
        });
    }, []);

    // Preview mode: admin can see app as a regular user would
    const previewAsUser = typeof window !== 'undefined' && localStorage.getItem('mom-preview-as-user') === 'true';
    if (previewAsUser && isAdmin) { isAdmin = false; }
    const hasFullAccess = isAdmin || isFriend;
    const visiblePrograms = programs.filter((prog) => isAdmin || prog.active !== false);
    const allPrograms = visiblePrograms.map((p) => ({
        ...p,
        _purchased: hasFullAccess || purchasedIds.includes(p.id),
    }));

    return (
        <div className="mom-page-shell" style={{ background: 'var(--mom-cream)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
            <AppNav demoMode={demoMode} />
            <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
                <div style={{ marginBottom: 40 }}>
                    <h1 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(28px, 4vw, 36px)', fontStyle: 'italic', marginBottom: 8, color: 'var(--mom-charcoal)' }}>Available Programs</h1>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, color: 'var(--mom-warm-gray)' }}>Structured programs designed to get real results.</p>
                </div>
                {loading ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
                        {[1, 2, 3].map((i) => <div key={i} className="mom-skeleton" style={{ height: 340, borderRadius: 24 }} />)}
                    </div>
                ) : allPrograms.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
                        {allPrograms.map((prog, i) => (
                            <ProgramCard key={prog.id} program={prog} purchased={prog._purchased} delay={i * 0.1} onClick={() => navigate(`/program/${generateSlug(prog.name)}`)} pods={podsByProgram[prog.id]} userId={userId} />
                        ))}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 18, color: 'var(--mom-warm-gray)' }}>Programs coming soon!</p>
                    </div>
                )}
            </div>
        </div>
    );
}

function ProgramCard({ program, purchased, delay, onClick, pods, userId }) {
    const [hovered, setHovered] = useState(false);
    const isCohort = program.programType === 'cohort';
    const availPods = pods ? getAvailablePods(pods) : [];
    const nextPod = availPods[0];
    const spotsLeft = nextPod ? (nextPod.maxParticipants || 0) - (nextPod.participants?.length || 0) : 0;
    // Check if there are pods the user isn't already in (for re-enrollment hint)
    const userInPodIds = new Set((pods || []).filter((p) => p.participants?.includes(userId)).map((p) => p.id));
    const hasNewGroups = isCohort && purchased && availPods.some((p) => !userInPodIds.has(p.id));

    return (
        <div style={{ position: 'relative' }}>
            {/* Glow behind card */}
            <div style={{
                position: 'absolute', top: '-10%', left: '8%', right: '8%', bottom: '-8%',
                background: 'radial-gradient(ellipse 90% 60% at 40% 35%, color-mix(in srgb, var(--mom-primary) 12%, transparent) 0%, transparent 70%)',
                pointerEvents: 'none', zIndex: 0,
                opacity: hovered ? 1 : 0.4,
                transition: 'opacity 0.3s ease',
            }} />
            <div className="slide-up" onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
                style={{
                    position: 'relative', zIndex: 1,
                    background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
                    borderRadius: 24, overflow: 'hidden', cursor: 'pointer',
                    border: '1px solid rgba(255,255,255,0.55)',
                    boxShadow: hovered ? 'var(--shadow-lg)' : 'var(--shadow-sm)',
                    transform: hovered ? 'translateY(-4px)' : 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    animationDelay: `${delay}s`,
                }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'radial-gradient(ellipse 70% 80% at 25% 0%, rgba(255,255,255,0.28) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 1, borderRadius: '24px 24px 0 0' }} />
                <div style={{ background: purchased ? 'var(--mom-charcoal)' : 'var(--mom-cream)', overflow: 'hidden' }}>
                    {program.imageUrl ? (
                        <img src={program.imageUrl} alt={program.name} style={{ width: '100%', height: 'auto', display: 'block' }} referrerPolicy="no-referrer" />
                    ) : null}
                </div>
                {purchased && (
                    <div style={{ position: 'absolute', top: 12, right: 12, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', color: 'var(--mom-white)', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '5px 14px', borderRadius: 20, letterSpacing: '0.04em', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 2 }}>ENROLLED</div>
                )}
                {isCohort && !purchased && (
                    <div style={{ position: 'absolute', top: 12, left: 12, background: 'var(--mom-charcoal)', color: 'var(--mom-white)', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 20, zIndex: 2 }}>COHORT</div>
                )}
                <div style={{ padding: '24px 28px 28px', position: 'relative', zIndex: 2 }}>
                    <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, marginBottom: 8, color: 'var(--mom-charcoal)' }}>{program.name}</h3>
                    <FormattedText text={program.description} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', marginBottom: 16 }} />
                    {isCohort && !purchased && nextPod && (() => {
                        const saleStatus = getPodSaleStatus(nextPod);
                        return (
                            <div className="mom-inset" style={{ padding: '10px 14px', marginBottom: 16 }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-charcoal)' }}>
                                    Next Up: <strong>{nextPod.podName}</strong> — starts {new Date(nextPod.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </p>
                                {saleStatus === 'waitlist' ? (
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-primary)', fontWeight: 600, marginTop: 2 }}>
                                        Waitlist open · Enrollment starts {new Date(nextPod.saleStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </p>
                                ) : saleStatus === 'on-sale' ? (
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-success)', fontWeight: 600, marginTop: 2 }}>
                                        Enrollment open{spotsLeft <= 3 ? ` · ${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} remaining` : ''}
                                    </p>
                                ) : (
                                    spotsLeft <= 3 ? (
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-primary)', fontWeight: 600, marginTop: 2 }}>
                                            {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} remaining
                                        </p>
                                    ) : null
                                )}
                            </div>
                        );
                    })()}
                    {program.highlights && program.highlights.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            {program.highlights.slice(0, 3).map((h, i) => (
                                <div key={i} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)', padding: '3px 0' }}>
                                    <span style={{ color: 'var(--mom-primary)', marginRight: 8 }}>✓</span>{h}
                                </div>
                            ))}
                        </div>
                    )}
                    {purchased ? (
                        <div>
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-link)' }}>Continue program →</span>
                            {hasNewGroups && (
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', marginTop: 6 }}>New groups forming — join another round</p>
                            )}
                        </div>
                    ) : (() => {
                        const hasPurchasable = isCohort && availPods.some((p) => { const s = getPodSaleStatus(p); return s === 'immediate' || s === 'on-sale'; });
                        const onlyWaitlist = isCohort && availPods.length > 0 && !hasPurchasable;
                        const noPods = isCohort && availPods.length === 0;
                        if (onlyWaitlist || noPods) {
                            return (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-link)', fontWeight: 600 }}>Join waitlist →</span>
                                </div>
                            );
                        }
                        return (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontWeight: 700, color: 'var(--mom-primary)' }}>{`$${program.price} USD`}</span>
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-link)', fontWeight: 600 }}>Learn more →</span>
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
}

export default Programs;